import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import * as CD from 'app/cd/models/essette-api';
import { bindable } from 'aurelia-typed-observable-plugin';
import { MemberService, MemberType } from 'app/cd/services/member-service';
import { apiCall } from 'app/shared/lib/api/api';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { deepClone } from 'app/shared/lib/utilities';

@autoinject()
export class MemberContactsCustomElement extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;

    protected memberType: MemberType = MemberType.Member;
    protected member: CD.Member | null = null;
    protected memberContacts: ReadonlyArray<CD.MemberContact> = [];

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(private readonly memberService: MemberService,
        ...rest: any[]) {
        super(...rest);

        if(this.env.application.code === 'Portal.Providers') {
            this.memberType = MemberType.Patient;
        }
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        this.loadMemberContacts();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    ////****************** Functions ************************************************////




    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMemberContacts(): Promise<void> {
        if(!this.memberId) return;

        const includes: string[] = [
            'addresses',
            'phoneNumbers',
            'relatedMember.addresses',
            'relatedMember.phoneNumbers'
        ];

        const fields: string[] = [
            'addresses.city',
            'addresses.postalCode',
            'addresses.region.code',
            'addresses.street1',
            'addresses.street2',
            'description',
            'phoneNumbers.number',
            'phoneNumbers.systemCode',
            'relatedMember.addresses.city',
            'relatedMember.addresses.postalCode',
            'relatedMember.addresses.region.code',
            'relatedMember.addresses.street1',
            'relatedMember.addresses.street2',
            'relatedMember.addresses.systemCode',
            'relatedMember.phoneNumbers.number',
            'relatedMember.phoneNumbers.systemCode'
        ];

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            sorting: this.sorting,
            page: this.$pager?.page || 0,
            top: this.pageSize
        };

        try {
            this.state.beginLoad();

            const response = this.memberType === MemberType.Patient ? await this.memberService.loadPatientContacts(this.memberId, options, includes, fields) : await this.memberService.loadMemberContacts(this.memberId, options, includes, fields);
            var memberContactsFromDB = deepClone(response.results);
            //The loadMemberContacts API does not return addresses and phonenumbers for contacts who are also members.
            //so we must fill those in from the relatedMember.
            memberContactsFromDB.filter(c => c.relatedMember).forEach(c => {
                c.addresses = c.relatedMember?.addresses?.filter(a => a.systemCode === 'CD.Member.Mailing');
                c.phoneNumbers = c.relatedMember?.phoneNumbers?.filter(pn => pn.systemCode === 'CD.Member.Home' || pn.systemCode === 'CD.Member.Cell' || pn.systemCode === 'CD.Member.Fax');
            })
            this.memberContacts = memberContactsFromDB;
            this.resultCount = response.count || 0;

        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
